import { t, type StringKey } from '../strings';

/**
 * 🔒 The one name each document kind goes by, in Title Case (T116, T171;
 * decision 0638). The product owner, 2026-09-20: *"PDF Title Case is wrong it
 * List things like 'Purchase Order' as 'Purchase order'"*; and 2026-09-22: *"Do
 * not name Bill template it is Bill Template this is title case always by
 * definition it is a title bar!"*.
 *
 * 🔨 A kind's name is read from here and nowhere else: the sheet's title, a
 * list's banner and its + New, the approvals queue, global search, the number
 * series, a template's title and every title bar that names a kind. 💰 Five
 * copies stood before this map, in two casings, and the sheet a customer
 * receives carried the sentence-case one.
 *
 * 🔨 A block caption on the sheet stays in sentence case (decision 0560). A
 * caption labels a block; this is the document's proper name.
 */
interface DocumentNameKeys {
  /** The name of one, as a title and a pill read it. */
  one: StringKey;
  /** The name of the list, as a list's banner reads it. */
  many: StringKey;
}

export const DOCUMENT_NAME_KEYS = {
  Quote: { one: 'documentName.quote', many: 'documentNames.quote' },
  SalesOrder: { one: 'documentName.salesOrder', many: 'documentNames.salesOrder' },
  Invoice: { one: 'documentName.invoice', many: 'documentNames.invoice' },
  PurchaseOrder: { one: 'documentName.purchaseOrder', many: 'documentNames.purchaseOrder' },
  WorkOrder: { one: 'documentName.workOrder', many: 'documentNames.workOrder' },
  // 🔒 Decision 2910: the visit's Service Report, a document of the visit numbered off its job.
  ServiceReport: { one: 'documentName.serviceReport', many: 'documentNames.serviceReport' },
  Bill: { one: 'documentName.bill', many: 'documentNames.bill' },
  CreditNote: { one: 'documentName.creditNote', many: 'documentNames.creditNote' },
  VendorCredit: { one: 'documentName.vendorCredit', many: 'documentNames.vendorCredit' },
  ChangeOrder: { one: 'documentName.changeOrder', many: 'documentNames.changeOrder' },
  RFQ: { one: 'documentName.rfq', many: 'documentNames.rfq' },
  // 🔒 Decision 0862: the bill of materials prints under its own name.
  BOM: { one: 'documentName.bom', many: 'documentNames.bom' },
  Payment: { one: 'documentName.payment', many: 'documentNames.payment' },
  Shipment: { one: 'documentName.shipment', many: 'documentNames.shipment' },
  // Decision 1730: the box packed against an order before a shipment carries it.
  Package: { one: 'documentName.package', many: 'documentNames.package' },
  StockTransfer: { one: 'documentName.stockTransfer', many: 'documentNames.stockTransfer' },
  StockCount: { one: 'documentName.stockCount', many: 'documentNames.stockCount' },
  StockRevaluation: { one: 'documentName.stockRevaluation', many: 'documentNames.stockRevaluation' },
  ExpenseReport: { one: 'documentName.expenseReport', many: 'documentNames.expenseReport' },
  JournalEntry: { one: 'documentName.journalEntry', many: 'documentNames.journalEntry' },
  Check: { one: 'documentName.check', many: 'documentNames.check' },
  Statement: { one: 'documentName.statement', many: 'documentNames.statement' },
  // The two sheets whose name is not their record's: a Shipment prints as a packing slip and a Payment as a receipt (piece 27).
  PackingSlip: { one: 'documentName.packingSlip', many: 'documentNames.packingSlip' },
  PaymentReceipt: { one: 'documentName.paymentReceipt', many: 'documentNames.paymentReceipt' },
  // Decision 1484: a merge's approval request, the eighth approvable type, named in the approvals queue.
  MergeRequest: { one: 'documentName.mergeRequest', many: 'documentNames.mergeRequest' },
  // Decision 8960: a Confidential file's release, the ninth approvable type, named in the approvals queue and the Approval Rules editor.
  FileRelease: { one: 'documentName.fileRelease', many: 'documentNames.fileRelease' },
} as const satisfies Readonly<Record<string, DocumentNameKeys>>;

export type NamedDocumentKind = keyof typeof DOCUMENT_NAME_KEYS;

export function isNamedDocumentKind(kind: string): kind is NamedDocumentKind {
  return Object.prototype.hasOwnProperty.call(DOCUMENT_NAME_KEYS, kind);
}

/**
 * The kind's own name, Title Case. 🔨 A kind the map does not name answers its
 * stored identifier rather than nothing, so a gap reads as a gap on the screen
 * and the map's own test is what refuses it.
 */
export function documentName(kind: string): string {
  return isNamedDocumentKind(kind) ? t(DOCUMENT_NAME_KEYS[kind].one) : kind;
}

/** The kind's name for a list of them, Title Case: Purchase Orders. */
export function documentNamePlural(kind: string): string {
  return isNamedDocumentKind(kind) ? t(DOCUMENT_NAME_KEYS[kind].many) : kind;
}

/** A create screen's title bar: New and the kind's name, New Purchase Order. */
export function newDocumentTitle(kind: string): string {
  return `${t('documentName.new')} ${documentName(kind)}`;
}

/** An edit screen's title bar: Edit and the kind's name, Edit Invoice (T1691). */
export function editDocumentTitle(kind: string): string {
  return `${t('documentName.edit')} ${documentName(kind)}`;
}

/**
 * 🔒 The non-document record kinds a list page names, singular and in Title
 * Case (T2107; decision 2841). `LIST_PAGES[type].singular` reads these for
 * the kinds it lists, and `recordKindName` reads them first, so a Company is
 * Company on a list's New button and in a tagged-record line alike.
 */
export const RECORD_KIND_NAMES = {
  CompanyRecord: 'Company',
  PersonRecord: 'Person',
  LocationRecord: 'Location',
  SiteRecord: 'Site',
  Item: 'Item',
  Deal: 'Deal',
  Lead: 'Lead',
} as const;

/** Where a PascalCase identifier splits into words, an initialism kept whole: `RFQVendorResponse` is RFQ Vendor Response. */
function spacedWords(identifier: string): string[] {
  return identifier
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(' ')
    .filter((word) => word !== '');
}

const JOINING_WORDS = new Set(['for', 'of', 'and', 'the', 'a', 'an', 'to', 'in', 'on']);
const INITIALISM = /^[A-Z0-9]{2,}$/;

/**
 * 🔒 The one spoken name of any record kind, document or not (T682, T2107;
 * decision 2841). It reads the kind's own name first (a list page's singular,
 * or the document map above) and spaces any other PascalCase identifier, so
 * *SalesOrder* never reaches a person as written and *CompanyRecord* is
 * Company. 'title' is the name standing alone, in Title Case, as a label or a
 * title bar draws it; 'sentence' is the name in the middle of a sentence, in
 * lower case with an initialism (RFQ, BOM, W9) kept in capitals.
 */
export type RecordKindForm = 'title' | 'sentence';

export function recordKindName(kind: string, form: RecordKindForm = 'title'): string {
  // A standard abbreviation the identifier already is (RFQ, BOM) stays itself in a sentence (decision 1790).
  if (form === 'sentence' && INITIALISM.test(kind)) return kind;
  const own = Object.prototype.hasOwnProperty.call(RECORD_KIND_NAMES, kind)
    ? RECORD_KIND_NAMES[kind as keyof typeof RECORD_KIND_NAMES]
    : isNamedDocumentKind(kind)
      ? documentName(kind)
      : null;
  const words = (own ?? spacedWords(kind.replace(/(.)Record$/, '$1')).join(' ')).split(' ').filter((word) => word !== '');
  if (form === 'title') {
    return words.map((word, index) => (index > 0 && JOINING_WORDS.has(word.toLowerCase()) ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
  }
  return words.map((word) => (INITIALISM.test(word) ? word : word.toLowerCase())).join(' ');
}

/**
 * A page's or a sheet's title while the document has no number (T682, UI audit
 * rank 1): Draft and the kind's name, *Draft Invoice*. The number line beside it
 * says when a number is given; the placeholder sentence is never the title.
 */
export function draftDocumentTitle(kind: string): string {
  return `${t('documentName.draft')} ${documentName(kind)}`;
}

/**
 * 🔒 The standard abbreviations (decision 1790; the owner, 2026-10-03: *"Allow
 * abbreviations when space is short the standard abbreviations are PO WO SO RFQ
 * BOM."*). Five kinds carry one; every other kind has none and is never cut.
 */
export const DOCUMENT_ABBREVIATION_KEYS = {
  PurchaseOrder: 'documentAbbreviation.purchaseOrder',
  WorkOrder: 'documentAbbreviation.workOrder',
  SalesOrder: 'documentAbbreviation.salesOrder',
  RFQ: 'documentAbbreviation.rfq',
  BOM: 'documentAbbreviation.bom',
} as const satisfies Readonly<Record<string, StringKey>>;

/** The kind's standard abbreviation, or null where the kind has none. */
export function documentAbbreviation(kind: string): string | null {
  return Object.prototype.hasOwnProperty.call(DOCUMENT_ABBREVIATION_KEYS, kind)
    ? t(DOCUMENT_ABBREVIATION_KEYS[kind as keyof typeof DOCUMENT_ABBREVIATION_KEYS])
    : null;
}

/**
 * 🔨 The name a control too narrow for the full one draws: the abbreviation
 * where the kind has one, otherwise the full name. A component never writes
 * the letters itself and never lets the browser cut a word.
 */
export function documentNameWhenShort(kind: string, short: boolean): string {
  return (short ? documentAbbreviation(kind) : null) ?? documentName(kind);
}
